import React from 'react'
import { Add, Minus } from 'iconsax-react'
import { IconButton, useTheme } from '@mui/material'

import WidgetContainer from '@SonarCX/components/atoms/WidgetContainer'
import Button from '@SonarCX/components/atoms/Button2'
import ButtonSecondary from '@SonarCX/components/atoms/ButtonSecondary'
import CompletionRate from './HeaderWidgets/CompletionRate'
import TimeToCompletion from './HeaderWidgets/TimeToCompletion'
import OverdueTasks from './HeaderWidgets/OverdueTasks'
import CycleTime from './HeaderWidgets/CycleTime'
import LeadTime from './HeaderWidgets/LeadTime'
import BurnRate from './HeaderWidgets/BurnRate'
import OverdueHeader from './HeaderWidgets/Overdue'
import WorkInProgress from './HeaderWidgets/WorkInProgress'
import UpcomingDeadlines from './BodyWidgets/UpcomingDeadlines'
import DueToday from './BodyWidgets/DueToday'
import OverdueBody from './BodyWidgets/Overdue'
import OpenTasks from '../ConversationsTab/BodyWidgets/OpenTasks'
import TaskStatuses from '../ConversationsTab/BodyWidgets/TaskStatuses'
import TaskTypes from '../ConversationsTab/BodyWidgets/TaskTypes'

import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import useSavedValue from '@SonarCX/hooks/useSavedValue'
import useStyles from './styles'

const TasksTab: React.FC = () => {
  const theme = useTheme()

  const hasPermission = useAccess()

  const canViewTasks = React.useMemo(
    () => hasPermission([PERMS.TasksOwn, PERMS.TasksAudit]),
    [hasPermission]
  )

  const [open, setOpen] = useSavedValue<boolean>({
    key: 'dashboard_task_stats_open',
    defaultValue: 'false'
  })

  const [mineOnly, setMineOnly] = useSavedValue<boolean>({
    key: 'dashboard_task_stats_mine_only',
    defaultValue: 'false'
  })

  const FirstButton = React.useMemo(
    () => (mineOnly ? Button : ButtonSecondary),
    [mineOnly]
  )

  const SecondButton = React.useMemo(
    () => (mineOnly ? ButtonSecondary : Button),
    [mineOnly]
  )

  const toggle = React.useCallback(val => () => setMineOnly(val), [setMineOnly])
  const toggleOpen = React.useCallback(() => setOpen(old => !old), [setOpen])

  const classes = useStyles({ open })

  return (
    <>
      <WidgetContainer
        variant='header'
        visible={canViewTasks}
        numColumns={8}
        className={classes.headerRoot}
      >
        <IconButton
          data-testid='task-stats-expand'
          onClick={toggleOpen}
          className={classes.toggleBtn}
        >
          {open ? (
            <Minus size={theme.size.base} />
          ) : (
            <Add size={theme.size.base} />
          )}
        </IconButton>

        {open && (
          <>
            <CompletionRate />

            <TimeToCompletion />

            <OverdueTasks />

            <CycleTime />

            <LeadTime />

            <BurnRate />

            <OverdueHeader />

            <WorkInProgress />
          </>
        )}
      </WidgetContainer>

      <WidgetContainer
        variant='body'
        visible={canViewTasks}
        className={classes.bodyRoot}
      >
        <div className={classes.toggleRoot}>
          <FirstButton onClick={toggle(true)} className={classes.toggle}>
            My tasks
          </FirstButton>

          <SecondButton onClick={toggle(false)} className={classes.toggle}>
            All tasks
          </SecondButton>
        </div>

        <DueToday mineOnly={mineOnly} />

        <UpcomingDeadlines mineOnly={mineOnly} skipDays={1} upcomingDays={7} />

        <UpcomingDeadlines mineOnly={mineOnly} skipDays={8} upcomingDays={30} />

        <OverdueBody mineOnly={mineOnly} />

        {mineOnly && <OpenTasks />}

        <TaskStatuses mineOnly={mineOnly} />

        <TaskTypes mineOnly={mineOnly} />
      </WidgetContainer>
    </>
  )
}

export default TasksTab
